import { AccessTime, ErrorOutline, Sync } from '@mui/icons-material';
import { Alert, Grid, Tooltip } from '@mui/material';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import messages from '../../intl/messages';
import { getCurrentExam } from '../../store/model/selectors';
import formatDate from '../../utils/formatDate';

// Task 16782 (48h mobile resilience) - candidate may keep uploading the missing mobile
// recording chunk for up to 48h after the exam finishes. Reviewer-only banner, candidate never sees it.
// Older backends don't send these fields at all - resolve to null rather than guessing a fallback state.
const resolveStatus = (exam, intl) => {
	if (exam?.mobileUploadIncomplete !== true) {
		// Upload finished, video is being regenerated in the background
		if (exam?.mobilePendingChunks === 0 && exam?.mobileVideoUpdatePending === true) {
			return {
				severity: 'info',
				icon: <Sync fontSize="inherit" />,
				tooltip: messages.mobileVideoUpdatePendingTooltip,
				text: intl.formatMessage(messages.mobileVideoUpdatePending),
			};
		}
		return null;
	}

	const windowClosesAt = exam.mobileUploadWindowClosesAt;
	if (!!windowClosesAt && new Date(windowClosesAt).getTime() > Date.now()) {
		return {
			severity: 'warning',
			icon: <AccessTime fontSize="inherit" />,
			tooltip: messages.mobileUploadIncompleteWaitingTooltip,
			text: intl.formatMessage(messages.mobileUploadIncompleteWaiting, { datetime: formatDate(windowClosesAt) }),
		};
	}

	const pendingChunks = exam.mobilePendingChunks;
	return {
		severity: 'error',
		icon: <ErrorOutline fontSize="inherit" />,
		tooltip: messages.mobileUploadIncompletePermanentTooltip,
		text:
			intl.formatMessage(messages.mobileUploadIncompletePermanent) +
			(typeof pendingChunks === 'number'
				? ` ${intl.formatMessage(messages.mobileUploadIncompletePermanentChunks, { seconds: pendingChunks })}`
				: ''),
	};
};

const MobileUploadStatus = () => {
	const intl = useIntl();
	const exam = useSelector(getCurrentExam);

	const status = resolveStatus(exam, intl);
	if (!status) return null;

	// Grid item lives here (not in ExamPreview) so the null states leave no empty grid row behind
	return (
		<Grid item xs={12}>
			<Tooltip title={intl.formatMessage(status.tooltip)}>
				<Alert severity={status.severity} icon={status.icon} sx={{ marginBottom: '15px' }}>
					{status.text}
				</Alert>
			</Tooltip>
		</Grid>
	);
};

export default MobileUploadStatus;
